'use client';

import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface CollapsibleEmbedProps {
  children: React.ReactNode;
  /** Clamp the embed to this pixel height when its content is taller. */
  maxPx?: number;
  /** Label for the expand button. */
  label?: string;
}

/**
 * Height guard for inline article embeds that have no canonical page to link
 * out to (e.g. the CloudProviderCalculator variants behind `data-calculator`).
 * Tools and vendor pickers collapse to a <ToolPreviewCard>/<VendorPickerCard>
 * that links to their own page; calculators don't have one, so instead of
 * letting a tall embed swamp the article we clamp it to `maxPx` with a fade and
 * a one-way "Show full …" button. Short embeds (≤ maxPx) render untouched.
 *
 * Height is measured with a ResizeObserver so the clamp tracks the embed's real
 * rendered height (which can change as the user interacts with sliders/inputs).
 */
export default function CollapsibleEmbed({
  children,
  maxPx = 720,
  label = 'Show full content',
}: CollapsibleEmbedProps) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // scrollHeight reports full content height regardless of the maxHeight clamp,
    // so this stays accurate whether or not we're currently collapsed.
    const measure = () => setOverflowing(el.scrollHeight > maxPx + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxPx]);

  const clamped = overflowing && !expanded;

  return (
    <div className="not-prose my-6">
      <div
        ref={ref}
        className="relative overflow-hidden"
        style={clamped ? { maxHeight: maxPx } : undefined}
      >
        {children}
        {clamped && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent dark:from-gray-900" />
        )}
      </div>
      {clamped && (
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {label}
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
